<template lang="pug">
transition(
  name="fade"
  v-if="!audioDoc && !wordDoc"
)
  .video-title-wrapper.fill.bg-black.z-40.w-full(
    v-if="screen.video && !videoEnded && !shouldSkipVideo"
  )
    uc-videojs.h-full.max-h-lvh.max-w-full.video-title-video.relative(
      :autoScroll="screen['auto-scroll']"
      :captions="screen.captions"
      :id="'vid_' + sectionId"
      :screen="screen"
      :src="screen.video"
      controls=""
      type="application/x-mpegURL"
      isTitleVideo=true
      @ended="videoEnded = true"
    )

    button.skip-video.btn.btn-primary.absolute.bottom-11.right-2.p-4.corners.shadow-md.z-50.text-base(
      :class="{ 'hidden': videoEnded || shouldSkipVideo }"
      @click="videoEnded = true"
    )
      uc-text.me-1(text="Skip Video")
      uc-icon.text-lg(icon="eva:skip-forward-fill")

.fill.opacity-40.bg-gradient-to-b.bg-bottom.-z-1(
  class="from-gray-800/40 via-gray-800/50 to-gray-800/80"
  data-course-title-fill=""
  v-if="!screen['no-overlay']"
)

button.reload-video.btn.btn-primary.absolute.bottom-11.right-0.p-4.corners.shadow-md.z-50.text-base(
  :class="{ 'visible': videoEnded }"
  v-if="!audioDoc && !wordDoc && screen.video && videoEnded && !shouldSkipVideo"
  @click="videoEnded = false"
)
  uc-text.me-1(text="Reload Video")
  uc-icon.text-lg(icon="mdi:reload")

.screen-course-title.wrapper.items-center.min-h-uc-screen(
  :class="[{ 'screen-course-title--bookmark': bookmark }]"
  ref="elRef"
)
  .course-title-wrapper.relative.flex.flex-col.flex-1.gap-12.justify-center.z-10.self-stretch.break-words.items-start.mt-16.xs_mt-20.md_mt-0(:style="{ display: thumbnailType === 'noTitle' ? 'none' : undefined }")
    .logo-wrapper(:class="{ 'max-w-[20em] max-h-[20em]': !showVinciLogo }")
      uc-logo#course-title-logo.course-title-logo.w-auto.inline-block.object-contain(
        :class="[showVinciLogo ? 'h-[2.5em]' : 'h-full min-h-[5em]']"
        :style="{ display: thumbnailType === 'hero' || thumbnailType === 'tile' ? 'none' : undefined }"
      )

    uc-editable#course-title.course-title(
      :text="content.course.title"
      editPath="$course.title"
      k="course.title"
      type="h1"
    )

    uc-editable#course-subtitle.course-subtitle(
      :text="content.course.subtitle"
      editPath="$course.subtitle"
      k="course.subtitle"
      type="h2"
      v-if="content.course.subtitle"
    )

    #scroll-down(
      v-if="(showStartButton && !bookmarkAvailable && !audioDoc) || (showStartButton && editModeAvailable)"
      uc-word-skip=""
      :style="{ display: thumbnailType ? 'none' : undefined }"
    )
      uc-button.mt-3.btn.btn-primary.btn-lg.unlock.ripple
        uc-text(text="Start")

  .course-title-scroll-down-wrapper(
    :class="{ 'scroll-down-wrapper': !bookmarkAvailable && !passed && !failed }"
    v-if="!(showStartButton && !bookmarkAvailable) && !audioDoc"
    uc-word-skip=""
    :style="{ display: thumbnailType ? 'none' : undefined }"
  )
    #scroll-down.text-center.my-12
      .prose-stack.mx-auto.p-8.rounded-sm.bg-slate-200.flex.flex-col.gap-2(
        class="dark_bg-gray-900/60"
        v-if="(passed || failed) && !editModeAvailable"
      )
        uc-text.text-center.text-xl(
          text="You have completed this course."
          v-if="passed"
        )
        uc-text.text-center.text-xl(
          text="You have failed this course."
          v-else-if="failed"
        )
        uc-text.text-center.text-xl(
          text="You are welcome to review it."
        )
        uc-button.btn.btn-primary.unlock.mt-6
          uc-text.text-xl(text="Review")

      .course-title-resume.prose-stack.mx-auto.p-8.rounded-sm.text-slate-900.dark_text-slate-200(
        class="dark_bg-gray-900/60 bg-slate-100/50"
        v-else-if="bookmarkAvailable && !editModeAvailable"
      )
        uc-text.text-center.text-xl(
          text="You have saved progress in this course."
        )
        p.mt-8
          uc-button.btn.btn-primary(@click="goToBookmark")
            uc-text.text-xl(text="Resume")

      .prose-stack(v-else-if="!showStartButton")
        uc-preload-sfx(name="welcome")
        button.scroll-down-button.unlock.inline-block.text-white.p-2.focus-visible_ring(
          @click="scrollDown"
          data-sfx="welcome"
          :aria-label="hasTranslation('Continue')"
          :style="{ display: thumbnailType ? 'none' : undefined }"
        )
          span.scroll-down-icon-wrapper.text-center.inline-block.text-6xl.mb-4.block(
            :class="{ 'animate-bounce': !suspend.reducedMotion }"
          )
            uc-icon.scroll-down-icon.text-primary-500(icon="theme:down")
          span.scroll-down-text-wrapper.text-center.prose-stack.font-semibold.title-scroll-text.block(
            :style="{ 'text-shadow': screen['no-overlay'] ? null : 'rgb(var(--color-gray-800) / 50%) 0px 0px 10px, rgb(var(--color-gray-800) / 50%) 0px 0px 15px, rgb(var(--color-gray-800) / 50%) 0px 0px 20px' }"
          )
            uc-text.tracking-wide(
              k="uc.6017541529969642"
              text="Scroll to continue"
            )
</template>
<script lang="ts">
export default {
  inheritAttrs: false
};
</script>

<script setup lang="ts">
import gsap from 'gsap';
import { throttle } from 'lodash-es';
import { inject, onMounted, ref, unref, computed, toRef } from 'vue';
import { getCourseId, getFirm, getLessonStatus } from '../lib/SCORM';
import useContent from '../stores/useContent';
import { storeToRefs } from 'pinia';
import useEditMode from '../stores/useEditMode';
import useScreenAudio from '../lib/useScreenAudio';
import courseTitleSchema from '../lib/screen-schemas/screen-course-title.schema';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import type VideoJs from 'video.js';
import 'video.js/dist/video-js.css';
import { watch } from 'vue';
import useTranslations from '../stores/useTranslations';
import useSuspend from '../lib/useSuspend';
import isHidden from '../lib/isHidden';

const { compiled } = storeToRefs(useContent());
const logo = computed(() => compiled.value.course.logo);
const passed = computed(() => getLessonStatus() === 'passed');
const failed = computed(() => getLessonStatus() === 'failed');
const props = defineProps<{
  editPath: string;
  screen: { required: true; type: Object };
}>();

const { playSlot, queueSlot } = useScreenAudio(
  toRef(props, 'screen'),
  courseTitleSchema
);

const { bookmark } = inject('data') as Data;
const goToBookmark = inject('goToBookmark');
const hasAudio = inject('hasAudio');
const audioDoc = inject('audioDoc');
const wordDoc = inject('wordDoc') as Ref<boolean>;
const thumbnailType = inject('thumbnailType') as string | null;
const pageId = inject('pageId') as object;
const sectionId = inject('sectionId');
const moreButtons = inject('moreButtons');
const sectionActive = inject<Ref<boolean>>('sectionActive', ref(false));
const showStartButton = computed(() => props.screen.start || moreButtons.value);

const showVinciLogo = computed(
  () => logo.value === true || logo.value === undefined
);

const videoEnded = ref(false);

// Skip video if thumbnail parameter is present
const shouldSkipVideo = computed(() => Boolean(thumbnailType));

const content = compiled;
const elRef = ref<HTMLElement>();
const firm = getFirm();
const courseId = getCourseId();
const { available: editModeAvailable, mode } = storeToRefs(useEditMode());
const bookmarkAvailable = bookmark && !isHidden(bookmark);

const suspend = useSuspend();
const { translations } = useTranslations();

const tl = gsap
  .timeline({
    paused: true,
    onComplete: () => {
      window.animatedTitle = true;
    }
  })
  .set('.bottom-left-logo', { autoAlpha: 0 });

function scrollDown() {
  const el = unref(elRef);
  const page = el?.closest('.page');
  page.unlockAndScroll(props.screen.id);
}

function hasTranslation(key, fallback) {
  // get the translation if it exists
  let maybeTranslated =
    translations?.[suspend.locale]?.[key] ||
    translations?.[suspend.locale]?.[fallback];

  return maybeTranslated || key;
}

function fadeInScreen() {
  tl.restart();
}

onMounted(async () => {
  const el = unref(elRef);

  let onMove = (e: MouseEvent) => {
    const pctX = e.clientX / window.innerWidth;
    const pctY = e.clientY / window.innerHeight;

    gsap.to('[data-page-parallax="welcome"] .parallax-bg', {
      xPercent: 2 - 4 * pctX,
      rotateY: -1 + 2 * pctX,
      duration: 12,
      ease: 'power3'
    });

    gsap.to('[data-page-parallax="welcome"] .parallax-bg', {
      yPercent: -2 * pctY,
      scaleY: 1 - 0.02 * pctY,
      rotateX: 5 * pctY,
      duration: 6,
      ease: 'power4'
    });
  };

  el.addEventListener('mousemove', throttle(onMove, 200, { leading: true }));

  function playAudio() {
    if (!sectionActive.value) return;
    // don't play course title audio if we have an intro video first, or it's word mode
    if ((props.screen?.video && !videoEnded.value) || wordDoc.value) return;

    const course = compiled.value.course;
    playSlot('intro', course?.title, 'course.title');
    if (course?.subtitle)
      queueSlot('intro', course?.subtitle, 'course.subtitle');
  }

  let logoTransformOrigin = '0 0';

  // set the logo transform origin to center if the course title wrapper is centered
  const courseTitleWrapper = el.querySelector('.course-title-wrapper');
  const wrapperStyles = window.getComputedStyle(courseTitleWrapper);

  if (
    courseTitleWrapper?.classList.contains('items-center') ||
    wrapperStyles.getPropertyValue('align-items') === 'center'
  ) {
    logoTransformOrigin = 'center';
  }

  tl.fromTo(
    '[data-course-title-fill]',
    { opacity: 0 },
    {
      duration: 1,
      opacity: 1,
      ease: 'linear'
    },
    0
  )
    .set(
      '#course-title-logo',
      { scale: 1.5, transformOrigin: logoTransformOrigin },
      0.5
    )
    .fromTo(
      '#course-title-logo',
      { autoAlpha: 0 },
      { autoAlpha: 1, duration: 1, ease: 'linear' },
      '>0.2'
    )
    .fromTo('#course-title-logo', { y: 200 }, { y: 0, duration: 1 }, '<')
    .to('#course-title-logo', { scale: 1, duration: 1 }, '>')
    .fromTo(
      '#course-title',
      { opacity: 0 },
      { opacity: 1, duration: 1, ease: 'linear' },
      '<'
    )
    .fromTo(
      '#course-title',
      { yPercent: -100 },
      { yPercent: 0, duration: 1 },
      '<'
    )
    .fromTo(
      '#course-subtitle',
      { opacity: 0 },
      { opacity: 1, duration: 1, ease: 'linear' },
      '>'
    )
    .add('afterTitles', '<')
    .call(playAudio, undefined, window.animatedTitle ? '>0.1' : '<');

  if (!window.animatedTitle) {
    tl.fromTo(
      '#scroll-down, .nav-buttons-container button',
      { opacity: 0 },
      { opacity: 1, duration: 1.0, ease: 'linear', stagger: 0.25 },
      '<'
    )
      .fromTo(
        '.nav-buttons-container button',
        { x: -100 },
        { x: 0, duration: 1.0, ease: 'elastic.out(1.5,1)', stagger: 0.25 },
        '<'
      )
      .fromTo(
        '.uc-language-widget',
        { opacity: 0 },
        { opacity: 1, duration: 1.0, ease: 'linear' },
        '<'
      )
      .from('.uc-language-widget', { yPercent: 100, duration: 1.0 }, '<');
  }

  tl.fromTo(
    '.uc-edit-bar',
    { opacity: 0, yPercent: -100 },
    { opacity: 1, yPercent: 0, duration: 1 },
    // FIXME restore the delay
    // FIXME is course-title required in every course?
    0
    // '>'
  );

  gsap.set('.bottom-left-logo', { autoAlpha: 0 });

  gsap
    .timeline({
      scrollTrigger: {
        trigger: el,
        start: '100% 50%',
        end: 'bottom 0%',
        toggleActions: 'play none play reverse'
      }
    })
    .to('.bottom-left-logo', {
      autoAlpha: 1,
      overwrite: true
    });

  gsap
    .timeline({
      scrollTrigger: {
        scroller: el.closest('.scroller'),
        trigger: el,
        scrub: true,
        start: '100% 80%',
        end: '100% 20%'
      }
    })
    .to('[data-page-background="welcome"]', {
      blur: 5,
      ease: 'linear'
    });

  if (window.animatedTitle) {
    tl.seek('afterTitles');
  }
  tl.play();
  window.animatedTitle = true;
  watch(videoEnded, (newValue, oldValue) => {
    if (newValue) {
      tl.restart();
    }
  });

  // Replay the title narration (and stop any audio still playing from a later
  // screen) whenever the learner scrolls back to the title. The intro timeline
  // only fires playAudio on first mount, so without this, returning to the
  // title never calls play() and the previous screen's narration keeps playing.
  watch(sectionActive, active => {
    if (active) playAudio();
  });

  const app = document.querySelector('#app');
  app?.addEventListener('restartTitleAnim', async e => {
    fadeInScreen();
  });
});
</script>
<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 3.5s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
